import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getLiveFolders, LiveFolder } from '../api/mailbox';
import { getMailAccounts, MailAccount } from '../api/auth';

export interface LiveFolderDest {
  account: number;
  imap_name: string;
  id: number;
  name: string;
}

interface FolderTransferDialogProps {
  open: boolean;
  mode: 'move' | 'copy';
  onClose: () => void;
  /** Preferred: live IMAP destination */
  onSelectLive?: (dest: LiveFolderDest) => Promise<void> | void;
  /** Legacy DB folder id (search results) */
  onSelect?: (folderId: number) => Promise<void> | void;
  excludeFolderId?: number | null;
  excludeImapFolder?: string | null;
}

export default function FolderTransferDialog({
  open,
  mode,
  onClose,
  onSelectLive,
  onSelect,
  excludeFolderId,
  excludeImapFolder,
}: FolderTransferDialogProps) {
  const [selected, setSelected] = useState<LiveFolderDest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const { data: foldersData, isLoading: foldersLoading } = useQuery({
    queryKey: ['live-folders'],
    queryFn: () => getLiveFolders(),
    enabled: open,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: getMailAccounts,
    enabled: open,
  });

  const accounts = accountsData?.results || [];
  const folders = (foldersData?.results || []).filter(
    (f) => f.id !== excludeFolderId && f.imap_name !== excludeImapFolder
  );

  const accountName = useMemo(() => {
    const map = new Map<number, string>();
    accounts.forEach((a: MailAccount) => map.set(a.id, a.name || a.email_address));
    return map;
  }, [accounts]);

  const grouped = useMemo(() => {
    const byAccount = new Map<number, LiveFolder[]>();
    folders.forEach((f) => {
      const list = byAccount.get(f.account) || [];
      list.push(f);
      byAccount.set(f.account, list);
    });
    byAccount.forEach((list) => {
      list.sort((a, b) => a.imap_name.localeCompare(b.imap_name));
    });
    return [...byAccount.entries()].sort((a, b) => {
      const na = accountName.get(a[0]) || String(a[0]);
      const nb = accountName.get(b[0]) || String(b[0]);
      return na.localeCompare(nb);
    });
  }, [folders, accountName]);

  const handleConfirm = async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      if (onSelectLive) await onSelectLive(selected);
      else if (onSelect) await onSelect(selected.id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'move' ? 'Move to…' : 'Copy to…'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose a destination folder on the mail server.
        </Typography>
        {foldersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            {grouped.map(([accountId, list]) => (
              <Box key={accountId} sx={{ mb: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                  {accountName.get(accountId) || `Account ${accountId}`}
                </Typography>
                {list.map((f) => (
                  <ListItemButton
                    key={`${f.account}:${f.imap_name}`}
                    selected={
                      selected?.account === f.account && selected?.imap_name === f.imap_name
                    }
                    onClick={() =>
                      setSelected({
                        account: f.account,
                        imap_name: f.imap_name,
                        id: f.id,
                        name: f.name,
                      })
                    }
                  >
                    <ListItemText primary={f.name} secondary={f.imap_name} />
                  </ListItemButton>
                ))}
              </Box>
            ))}
            {!folders.length && (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No destination folders available.
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!selected || busy}>
          {busy ? <CircularProgress size={18} /> : mode === 'move' ? 'Move' : 'Copy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
